import { ng } from "../ngds";

// Source of truth for shared application types used by both client and server.
// Local sharedTypes files in each app should only re-export from this module.

export type Settings = IUserSettings;

export type SupportAxis = "Y" | "Z";
export type SupportConnectivityType = "rigid" | "pinned" | "roller" | "free" | "rigid_roller";

export const classifySupport = (sup: ng.stage.Support, axis: SupportAxis, isBase = false): SupportConnectivityType => {
  const con = sup.con;
  if (!con) return "free";

  const transY = con.transY?.value || 0;
  const transZ = con.transZ?.value || 0;
  const rotY = con.rotY?.value || 0;
  const rotZ = con.rotZ?.value || 0;

  if (axis === "Y") {
    // axis Y: lateral transY, rotation rotZ (independent of transX)
    if (transY === -1 && rotZ === -1) return isBase ? "rigid" : "rigid_roller";
    if (transY === -1 && rotZ === 0) return isBase ? "pinned" : "roller";
    if (transY === 0 && rotZ === -1) return "rigid_roller";
    return "free";
  }

  if (axis === "Z") {
    // axis Z: lateral transZ, rotation rotY (independent of transX)
    if (transZ === -1 && rotY === -1) return isBase ? "rigid" : "rigid_roller";
    if (transZ === -1 && rotY === 0) return isBase ? "pinned" : "roller";
    if (transZ === 0 && rotY === -1) return "rigid_roller";

    return "free";
  }

  return "rigid";
};
// --- Material related interfaces ---

export type WebResponse<TData> =
  | {
      kind: "data";
      data: TData;
      pagination: {
        CurrentPage: number;
        PageSize: number;
        TotalItemCount: number;
        TotalPageCount: number;
      };
      status: number;
    }
  | { kind: "error"; error: unknown; status: number };

export type WebResponseNoPagination<TData> =
  | {
      kind: "data";
      data: TData;
      status: number;
    }
  | { kind: "error"; error: unknown; status: number };

export type MaterialsFetchGuidResponse = WebResponseNoPagination<ng.material.Data_json[]>;

interface TMaterialInfo<TKind, TType, TData, TSubType> {
  kind: TKind;
  type: TType;
  data: TData;
  subType: TSubType;
}
export type MaterialInfo =
  | TMaterialInfo<"concrete", ng.material.concrete.Type, ng.material.concrete.CharacteristicData | null, "">
  | TMaterialInfo<"steel", ng.material.steel.Sort, ng.material.steel.CharacteristicData | null, "">
  | TMaterialInfo<"timber", "kSolid", ng.material.timber.solid.CharacteristicData, "solid_">
  | TMaterialInfo<"timber", "kBoard", ng.material.timber.board.CharacteristicData, "board_">
  | TMaterialInfo<"reinforcement", "kBar" | "kWire", ng.material.reinforcement.CharacteristicData | null, "">
  | TMaterialInfo<"fireSteel", ng.material.Mtrl, ng.fire_protection.steel.CharacteristicData | null, "">;
export type MaterialInfo_json =
  | TMaterialInfo<"concrete", ng.material.concrete.Type, ng.material.concrete.CharacteristicData_json | null, "">
  | TMaterialInfo<"steel", ng.material.steel.Sort, ng.material.steel.CharacteristicData_json | null, "">
  | TMaterialInfo<"timber", "kSolid", ng.material.timber.solid.CharacteristicData_json, "solid_">
  | TMaterialInfo<"timber", "kBoard", ng.material.timber.board.CharacteristicData_json, "board_">
  | TMaterialInfo<"reinforcement", "kBar" | "kWire", ng.material.reinforcement.CharacteristicData_json | null, "">
  | TMaterialInfo<"fireSteel", ng.material.Mtrl, ng.fire_protection.steel.CharacteristicData_json | null, "">;

export type ConversionValuesType = string | ng.material.concrete.Type | ng.material.steel.Sort | ng.material.Mtrl;

// type subType are now part of MaterialInfo, see ConversionValues type
// for vals use helper function extractValsFromMaterialInfo in mtrl/fetch
export interface IConversionValues {
  dbMtrl: ng.material.Data;
  id: ng.utils.ID | null;
  materialCategory: ng.material.Mtrl;
}
export interface IConversionValues_json {
  dbMtrl: ng.material.Data_json;
  id: ng.utils.ID_json | null;
  materialCategory: ng.material.Mtrl;
}
export type ConversionValues = IConversionValues & MaterialInfo;
export type ConversionValues_json = IConversionValues_json & MaterialInfo_json;
export type RestMaterial = WebResponseNoPagination<ConversionValues_json>;

// party based on the c# class from ng-asset\AssetService\Controllers\MaterialAssetController.cs
export interface MaterialListEntry {
  guid: string;
  id: string;
  name: string;
  type: string;
  ownertype: ng.utils.Owner;
  product: number;
  /** National annex the asset database files it under; read it as the country it is sold in. */
  annex?: ng.eurocode.Annex;
  dias: number[] | undefined; // only added by client
}

export type MaterialKey =
  | "Undefined"
  | "Steel"
  | "Concrete"
  | "Timber"
  | "Soil"
  | "Reinforcement"
  | "Fire protection steel";

// index needs to match
// ng-asset\AssetService\Controllers\MaterialAssetController.cs
export const MATERIAL_KEY_FROM_MATERIAL_INDEX: Array<MaterialKey> = [
  "Undefined",
  "Steel",
  "Concrete",
  "Timber",
  "Soil",
  "Reinforcement",
  "Fire protection steel",
];

/// A "list" of materials grouped on the key
export interface MaterialList {
  [key: string]: Array<MaterialListEntry> | undefined;
}

export type RestMaterialList = WebResponse<MaterialList>;

// --- User related interfaces ---

export interface IUserQuantityItem {
  name: string;
  unit: string;
  size: number;
}

export interface IUserQuantitySettings {
  standard: string;
  length: IUserQuantityItem;
  force: IUserQuantityItem;
  moment: IUserQuantityItem;
  lineload: IUserQuantityItem;
  angle: IUserQuantityItem;
  stress: IUserQuantityItem;
  strain: IUserQuantityItem;
  mass: IUserQuantityItem;
  sectional: IUserQuantityItem;
  temperature: IUserQuantityItem;
  deformation: IUserQuantityItem;
  energy: IUserQuantityItem;
  power: IUserQuantityItem;
  utilisation: IUserQuantityItem;
}

export interface IUserColor {
  x: number;
  y: number;
  z: number;
  hex: string;
}

export interface IUserDefaultValues {
  company: string;
  material: ng.material.Mtrl;
  nationalAnnex: ng.eurocode.Annex;
  signature: string;
  generation: ng.eurocode.Generation;
}

// --- Print Visibility ---

export interface IPrintComponentVisibility {
  label: string;
  status: boolean;
}

export interface IPrintLayoutVisibility {
  label: string;
  status: boolean;
  components: IPrintComponentVisibility[];
}

export interface IPrintExtentVisibility {
  label: string;
  layouts: IPrintLayoutVisibility[];
}

export const DEFAULT_GRAPH_SCALE = 0.2;

export interface IUserSettings {
  tempProject: any;
  gridSquareSize: number;
  analyseOnSave: boolean;
  gridSquare: number;
  snapTogridSense: number;
  willSnapToGrid: boolean;
  filled: boolean;
  beamThickness: number;
  graphScaleFactor: number;
  graphNumberDecimal: number;
  graphNumberDecimalDeformations: number;
  invertZoomMouse: boolean;
  autoZoomViewportEnabled: boolean;
  defaultProject: number;
  lastSelectedLoadCombName: string;
  quantity: IUserQuantitySettings;
  gridLineThickness: number;
  implicitSupports: boolean;
  showSupportId: boolean;
  showLCS: boolean;
  showSection: boolean;
  showCross: boolean;
  pyshicalResp: boolean;
  proportionalLoads: boolean;
  showMaterial: boolean;
  showBeamId: boolean;
  showReactions: boolean;
  showGrid: boolean;
  hideUnusedLoads: boolean;
  lang: Lang;
  majorLineWidth: number;
  minorLineWidth: number;
  majorLineEach: number;
  darkMode: boolean;
  gridColorLight: IUserColor;
  lineColorLight: IUserColor;
  lineMajorColorLight: IUserColor;
  graphStaticColors: boolean;
  graphPositiveColorLight: IUserColor;
  graphNegativeColorLight: IUserColor;
  gridColorDark: IUserColor;
  lineColorDark: IUserColor;
  lineMajorColorDark: IUserColor;
  graphPositiveColorDark: IUserColor;
  graphNegativeColorDark: IUserColor;
  showDisclaimer: boolean;
  lastReleaseNoteShown: string;
  defaultValues: IUserDefaultValues;
  printVisibility: IPrintExtentVisibility[];
  graphScale: number;
  useHub: boolean; // deprecated
  showSurvicateSurvey: boolean;
  showSimplexCenterHint: boolean;
}

// --- Design reporter ---

export interface IReportedDesignChange {
  id: string;
  type: string;
  previous: string;
  suggested: string;
}

// --- Release notes ---

export interface IReleaseEntry {
  version: string;
  date: string;
  notes: ILangItem[];
}

export interface IReleaseNotes {
  releases: IReleaseEntry[];
}

// --- Localisation database ---

export interface IEntry {
  length?: IStandard | undefined;
  force?: IStandard | undefined;
  moment?: IStandard | undefined;
  lineload?: IStandard | undefined;
  angle?: IStandard | undefined;
  stress?: IStandard | undefined;
  strain?: IStandard | undefined;
  mass?: IStandard | undefined;
  sectional?: IStandard | undefined;
  temperature?: IStandard | undefined;
  deformation?: IStandard | undefined;
  energy?: IStandard | undefined;
  power?: IStandard | undefined;
  utilisation?: IStandard | undefined;
  label?: ILangItem | undefined;
  description?: ILangItem | undefined;
  quantity?: string[] | undefined;
  symbol?: IStandard | undefined;
  slug?: ILangItem | undefined;
}

interface IStandard {
  SI: string | INameSizeUnit;
  IMP: string | INameSizeUnit;
}
export type Std = "SI" | "IMP";

interface INameSizeUnit {
  name: string;
  unit: string;
  size: number | string;
}

export interface ILangItem {
  en: string;
  sv?: string | undefined;
  da?: string | undefined;
}

export type Lang = "en" | "sv" | "da" | "fi";

export interface IAPICheckResponse {
  access: boolean;
  status: number;
  statusText: string;
}

export interface ITranslationDatabase {
  [key: string]: IEntry;
}
